import { Group, Table } from '@mantine/core';
import React, { useMemo } from 'react';
import type { TransformedTarget } from '../../../api/targets-client/targets-client.type';
import { baseBlack } from '../../../styles/design-tokens';
import { Icon } from '../../../ui-library/icon/icon';
import { Tooltip } from '../../../ui-library/tooltip/tooltip';
import { Text } from '../../../ui-library/typography/typography';
import { getTargetComparisonSymbol } from '../../adherence/targets/targets.helpers';
import { TARGET_THRESHOLD_COLORS, TargetThreshold } from '../../adherence/targets/targets.type';
import { getTargetThresholdMessage } from './widget.helpers';

interface TableWidgetProps {
  data: {
    id: string;
    name: string;
    date: string;
    value: number;
  }[];
  selectedTarget: TransformedTarget | null;
  targetThresholds: Record<string, TargetThreshold | null>;
}

export const TableWidget: React.FC<TableWidgetProps> = ({ data, selectedTarget, targetThresholds }) => {
  const tableData = useMemo(
    () =>
      data.map((row) => {
        const targetValue = selectedTarget?.time_allocations?.[row.date];
        if (selectedTarget && targetValue && typeof targetValue === 'number') {
          return {
            ...row,
            target_value: `${targetValue}`,
          };
        } else if (selectedTarget && targetValue && typeof targetValue === 'object') {
          return {
            ...row,
            target_value: getTargetComparisonSymbol(
              selectedTarget.target_comparison,
              targetValue.value_lower,
              targetValue.value_upper,
            ),
          };
        }
        return {
          ...row,
          target_value: null,
        };
      }),
    [data, selectedTarget],
  );

  return (
    <Table>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Object Name</Table.Th>
          <Table.Th>Date</Table.Th>
          <Table.Th>Value</Table.Th>
          {selectedTarget && <Table.Th>Target Value</Table.Th>}
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {tableData.map((row) => {
          if (row.value > 0) {
            const threshold = targetThresholds[row.date];
            const thresholdColor = threshold ? TARGET_THRESHOLD_COLORS[threshold] : baseBlack;
            return (
              <Table.Tr key={`${row.id}-${row.date}`}>
                <Table.Td>{row.name}</Table.Td>
                <Table.Td>{row.date}</Table.Td>
                <Table.Td>
                  <Tooltip
                    label={
                      threshold && selectedTarget
                        ? getTargetThresholdMessage(selectedTarget.thresholds[row.date], threshold)
                        : `${row.value.toLocaleString()}`
                    }
                    variant="black"
                    multiline
                    zIndex={1000}
                  >
                    <Group gap={5}>
                      <Text size="small" weight="medium">
                        {row.value.toLocaleString()}
                      </Text>
                      {threshold && <Icon name="circle" variant="filled" color={thresholdColor} size={10} />}
                    </Group>
                  </Tooltip>
                </Table.Td>
                {selectedTarget && <Table.Td>{row.target_value}</Table.Td>}
              </Table.Tr>
            );
          }
          return null;
        })}
      </Table.Tbody>
    </Table>
  );
};
